import React, { useState, useEffect, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '../../../components/ui/Button';
import { LoadingState } from '../../../components/shared/LoadingState';
import { ErrorState } from '../../../components/shared/ErrorState';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Custom CSS to reduce PDF margins
const pdfStyle = `
  .react-pdf__Page {
    margin: 0 !important;
  }
  .react-pdf__Page__canvas {
    margin: 0 !important;
    display: block;
  }
`;

// Set up PDF.js worker
// For pdfjs-dist 5.x, use CDN worker file (.mjs format)
// Using absolute external URL prevents Vite from processing it as a module
if (typeof window !== 'undefined') {
  // Use jsdelivr CDN - absolute URL prevents Vite from adding ?import query parameter
  const workerVersion = pdfjs.version || '5.4.530';
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${workerVersion}/build/pdf.worker.min.mjs`;
}

interface PDFPreviewProps {
  file: string | File | Blob | ArrayBuffer;
  onClose?: () => void;
  showDownload?: boolean;
}

export const PDFPreview: React.FC<PDFPreviewProps> = ({
  file,
  onClose,
  showDownload = true,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfFileUrl, setPdfFileUrl] = useState<string | Blob | File | ArrayBuffer | null>(null);

  // Prepare PDF file for react-pdf
  // Use URL directly instead of fetching as blob to avoid ArrayBuffer detachment issues
  useEffect(() => {
    if (typeof file === 'string') {
      // Use URL string directly - react-pdf handles CORS better than we do
      setPdfFileUrl(file);
      setLoading(false);
    } else if (file instanceof Blob || file instanceof File) {
      // For Blob/File, create object URL to avoid ArrayBuffer issues
      const objectUrl = URL.createObjectURL(file);
      setPdfFileUrl(objectUrl);
      setLoading(false);

      // Cleanup object URL on unmount
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else {
      // ArrayBuffer or other types - use directly
      setPdfFileUrl(file);
      setLoading(false);
    }
  }, [file]);

  // Memoize document options to prevent unnecessary reloads
  const documentOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    httpHeaders: {},
    withCredentials: false,
  }), []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error);
    console.error('PDF file:', file);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
    });

    const errorMessage = error.message || 'Unknown error';
    setError(`Failed to load PDF document: ${errorMessage}`);
    setLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages, prev + 1));
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(3.0, prev + 0.25));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(0.5, prev - 0.25));
  };

  const handleDownload = () => {
    if (typeof file === 'string') {
      // For URL strings, create a link with download attribute to force download
      const link = document.createElement('a');
      link.href = file;
      link.download = 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (file instanceof Blob || file instanceof File) {
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (pdfFileUrl && typeof pdfFileUrl === 'string' && pdfFileUrl.startsWith('blob:')) {
      // If we have a blob URL, download it
      const link = document.createElement('a');
      link.href = pdfFileUrl;
      link.download = 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Show error state if there's an error
  if (error) {
    return (
      <div className="space-y-4">
        <ErrorState message={error} />
        {typeof file === 'string' && (
          <div className="text-center space-y-2">
            <Button variant="outline" onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
            <Button variant="outline" onClick={() => window.open(file, '_blank')}>
              Open PDF in New Tab
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <style>{pdfStyle}</style>
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            Page {pageNumber} of {numPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleZoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm w-16 text-center">{Math.round(scale * 100)}%</span>
          <Button variant="outline" size="sm" onClick={handleZoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          {showDownload && (
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="h-4 w-4" />
            </Button>
          )}
          {onClose && (
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>

      <div className="flex justify-center bg-gray-100 p-2 rounded-lg overflow-auto min-h-[400px] relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <LoadingState message="Loading PDF..." />
          </div>
        )}

        {/* Use react-pdf Document component */}
        {pdfFileUrl && (
          <Document
            file={pdfFileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
            options={documentOptions}
          >
            {numPages > 0 && (
              <Page
                pageNumber={pageNumber}
                scale={scale}
                className="shadow-lg"
                renderTextLayer={true}
                renderAnnotationLayer={true}
                width={undefined} // Let it use full width
              />
            )}
          </Document>
        )}
      </div>
    </div>
  );
};
